import React from "react";

import { css, cx } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { LanguageCode, Languages, languageTranslationMapping } from "@/intl/i18n.tsx";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { TooltipMenuAction } from "@/uiKit/atoms/TooltipMenu.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";

export type LanguageSelectorProps = {
  id?: string;
  display: "flag" | "full";
  className?: string;
};

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({ id, display, className }) => {
  const styles = useStyles(makeStyles);
  const { t, i18n } = useTranslation();

  return (
    <TooltipMenuAction
      popupId="language-selection-menu"
      placement="right"
      className={className}
      actions={Object.keys(Languages).map(lang => (
        <Button
          key={lang}
          variant="text"
          data-action="account.language.select"
          data-language={lang}
          onClick={() => i18n.changeLanguage(lang)}
          className={cx({ [styles.selected]: lang == i18n.language })}
        >
          {t(languageTranslationMapping(lang as LanguageCode))}
        </Button>
      ))}
    >
      <Button data-action="account.language.select" id={id} variant="text">
        {display === "full" ? (
          <Typography variant="h5">{t("languageFull")}</Typography>
        ) : (
          <Typography variant="h4">{t("languageFlag")}</Typography>
        )}
      </Button>
    </TooltipMenuAction>
  );
};

const makeStyles = (theme: Theme) => ({
  selected: css`
    background-color: ${theme.palette.action.selected};
    cursor: default;
    pointer-events: none;
  `,
});
